import { useEffect, useRef } from 'react';

import type { AnimationItem } from 'lottie-web';

import { logger } from '@/utils/logger/logger';

/**
 * Lottie animation data structure based on the Lottie JSON schema
 */
interface LottieAnimationData {
  /** Version of the Lottie format */
  v: string;
  /** Frame rate */
  fr: number;
  /** In point (start frame) */
  ip: number;
  /** Out point (end frame) */
  op: number;
  /** Width of the composition */
  w: number;
  /** Height of the composition */
  h: number;
  /** Name of the composition */
  nm?: string;
  /** Duration in frames */
  ddd?: number;
  /** Assets used in the animation */
  assets?: unknown[];
  /** Layers that make up the animation */
  layers: unknown[];
  /** Markers for the animation */
  markers?: unknown[];
  /** Additional metadata */
  [key: string]: unknown;
}

interface LottieAnimationProps {
  /** Lottie animation data object */
  animationData: LottieAnimationData;
  /** CSS class names for styling */
  className?: string;
  /** Whether the animation should loop */
  loop?: boolean;
  /** Whether the animation should autoplay */
  autoplay?: boolean;
}

const TAG = 'LottieAnimation:';

/**
 * Lottie animation component using dynamic import to avoid bundle bloat.
 *
 * Loads the lottie-web library only when needed and renders the specified
 * animation. Handles cleanup and error states gracefully.
 */
export function LottieAnimation({
  animationData,
  className = '',
  loop = true,
  autoplay = true,
}: LottieAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadAndPlayAnimation = async () => {
      try {
        // Dynamic import to avoid including lottie-web in main bundle
        const lottie = await import('lottie-web');

        if (!isMounted || !containerRef.current) {
          return;
        }

        // Create the animation using the imported animation data
        animationRef.current = lottie.default.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop,
          autoplay,
          animationData,
        });

        logger.debug(TAG, 'Animation loaded successfully');
      } catch (error) {
        logger.error(TAG, 'Failed to load Lottie animation:', error);
      }
    };

    loadAndPlayAnimation();

    return () => {
      isMounted = false;
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
  }, [animationData, loop, autoplay]);

  return <div ref={containerRef} className={className} />;
}
